import { api } from "@/api/api";
import { useUserInfo } from "@/api/user";
import { getApiErrorMessage } from "@/api/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { config } from "@/config";
import * as analytics from "@/utils/analytics";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Briefcase,
  Building,
  CheckCircle,
  Lightbulb,
  Settings,
  Target,
  TrendingUp,
  Wrench,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  firstName: z.string().min(1, { message: "Required" }),
  lastName: z.string().min(1, { message: "Required" }),
  email: z
    .string()
    .min(1, "Required")
    .email({ message: "Please enter a valid email address." }),
  jobTitle: z.string().min(1, { message: "Required" }),
  companyName: z.string().min(1, { message: "Required" }),
  companyUrl: z.string().min(1, { message: "Required" }),
  companySize: z.string().min(1, { message: "Required" }),
  industry: z.string().min(1, { message: "Required" }),
  keyApps: z
    .array(z.string())
    .min(1, { message: "Please select at least one app" }),
  otherApps: z.string().optional(),
  enterpriseNeeds: z.string().min(1, { message: "Required" }),
});

const apps = [
  {
    id: "gmail",
    name: "Gmail",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/gmail.svg",
  },
  {
    id: "outlook",
    name: "Outlook",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/outlook.svg",
  },
  {
    id: "slack",
    name: "Slack",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/slack.svg",
  },
  {
    id: "teams",
    name: "Teams",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/microsoft-teams.svg",
  },
  {
    id: "zoom",
    name: "Zoom",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/zoom.svg",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/hubspot.svg",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/salesforce.svg",
  },
  {
    id: "drive",
    name: "Google Drive",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/google-drive.svg",
  },
  {
    id: "notion",
    name: "Notion",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/notion.svg",
  },
  {
    id: "sharepoint",
    name: "SharePoint",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/sharepoint-online.svg",
  },
  {
    id: "calendar",
    name: "Google Calendar",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/google-calendar.svg",
  },
  {
    id: "shopify",
    name: "Shopify",
    iconUrl:
      "https://static.dev.cohesive.ai/images/integrations/logos/shopify.svg",
  },
];

const companySizes = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1000+ employees",
];

const industries = [
  "Technology",
  "Financial Services",
  "Hospital & Health Care",
  "Higher Education",
  "Retail",
  "Electrical/Electronic Manufacturing",
  "Management Consulting",
  "Entertainment",
  "Real Estate",
  "Other",
];

// Map display names to backend snake_case keys
const industryMap: { [key: string]: string } = {
  Technology: "INFORMATION_TECHNOLOGY_AND_SERVICES",
  "Financial Services": "FINANCIAL_SERVICES",
  "Hospital & Health Care": "HOSPITAL_HEALTH_CARE",
  "Higher Education": "HIGHER_EDUCATION",
  Retail: "RETAIL",
  "Electrical/Electronic Manufacturing": "ELECTRICAL_ELECTRONIC_MANUFACTURING",
  "Management Consulting": "MANAGEMENT_CONSULTING",
  Entertainment: "ENTERTAINMENT",
  "Real Estate": "REAL_ESTATE",
  Other: "OTHER",
};

const recaptchaReady = () =>
  new Promise<void>((resolve) => {
    grecaptcha.enterprise.ready(() => {
      resolve();
    });
  });

const getStepHeaderContent = (currentStep: number) => {
  switch (currentStep) {
    case 1:
      return {
        badge: "Enterprise Inquiry",
        title: "Tell us about your organization",
        subtitle:
          "Help us understand your current setup and requirements so we can provide you with a personalized Enterprise plan proposal.",
        features: [
          {
            icon: Building,
            title: "Enterprise-grade security",
            description: "SSO, SOC2 compliance, and advanced security features",
            color: "blue",
          },
          {
            icon: Settings,
            title: "Custom integrations",
            description: "Tailored solutions for your unique business needs",
            color: "green",
          },
          {
            icon: Target,
            title: "Dedicated support",
            description: "Customer Success Manager and priority SLA",
            color: "purple",
          },
        ],
        cta: "Share your organization details so we can better understand your Enterprise needs and provide a customized solution.",
      };
    case 2:
      return {
        badge: "Understanding Your Tech Stack",
        title: "What tools does your team use?",
        subtitle:
          "Understanding your current integrations helps us design the optimal Enterprise solution for your workflows.",
        features: [
          {
            icon: Zap,
            title: "Enhanced integrations",
            description:
              "Priority access to all current and future integrations",
            color: "yellow",
          },
          {
            icon: Wrench,
            title: "Custom connectors",
            description:
              "Build integrations for your proprietary or niche tools",
            color: "blue",
          },
          {
            icon: Brain,
            title: "Advanced workflows",
            description:
              "Complex automations across your entire tech ecosystem",
            color: "purple",
          },
        ],
        cta: "Select the tools your team relies on most. We'll show you how Enterprise features enhance these integrations.",
      };
    case 3:
      return {
        badge: "Final Step",
        title: "What are your Enterprise requirements?",
        subtitle:
          "Share your specific Enterprise needs so we can prepare a tailored proposal that addresses your organization's unique challenges.",
        features: [
          {
            icon: Target,
            title: "Tailored proposal",
            description:
              "Custom pricing and features based on your requirements",
            color: "red",
          },
          {
            icon: Settings,
            title: "Implementation support",
            description: "Dedicated onboarding and migration assistance",
            color: "blue",
          },
          {
            icon: TrendingUp,
            title: "Success metrics",
            description: "Define and track ROI specific to your organization",
            color: "green",
          },
        ],
        cta: "Tell us about your security requirements, compliance needs, scaling challenges, and any specific features you're looking for.",
      };
    default:
      return null;
  }
};

interface EnterpriseInquiryFormProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export function EnterpriseInquiryForm({
  onSuccess,
  onClose,
}: EnterpriseInquiryFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const { data: userInfo } = useUserInfo();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      jobTitle: "",
      companyName: "",
      companyUrl: "",
      companySize: "",
      industry: "",
      keyApps: [],
      otherApps: "",
      enterpriseNeeds: "",
    },
  });

  // Prepopulate form with user info when available
  useEffect(() => {
    if (userInfo) {
      form.reset({
        firstName: userInfo.user.first_name || "",
        lastName: userInfo.user.last_name || "",
        email: userInfo.user.email || "",
        jobTitle: userInfo.organization_user.metadata.job_title || "",
        companyName: userInfo.organization.name || "",
        companyUrl: "",
        companySize: "",
        industry: "",
        keyApps: [],
        otherApps: "",
        enterpriseNeeds: "",
      });
    }
  }, [userInfo, form]);

  const { mutate: submitInquiry, isPending } = useMutation({
    mutationFn: async (values: z.infer<typeof formSchema>) => {
      setError(null);

      await recaptchaReady();

      const token = await grecaptcha.enterprise.execute(
        config.recaptcha.siteKey,
        {
          action: "ENTERPRISE_INQUIRY",
        },
      );

      // Combine key apps with other apps for tech stack
      const allApps = [...values.keyApps];
      if (values.otherApps) {
        allApps.push(values.otherApps);
      }
      const techStack = allApps.join(", ");

      // Map company size to match backend format
      const companySizeMap: { [key: string]: string } = {
        "1-10 employees": "1-19",
        "11-50 employees": "20-49",
        "51-200 employees": "50-99",
        "201-500 employees": "100-250",
        "501-1000 employees": "251-1500",
        "1000+ employees": "1500+",
      };

      const payload = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        job_title: values.jobTitle,
        phone_number: "", // Empty since we're not collecting phone number
        company_name: values.companyName,
        company_size: companySizeMap[values.companySize] || values.companySize,
        company_industry: industryMap[values.industry] || values.industry,
        notes: values.enterpriseNeeds || "",
        pipeline_id: import.meta.env.VITE_ENTERPRISE_PIPELINE_ID,
        stage_id: import.meta.env.VITE_ENTERPRISE_STAGE_ID,
        website: values.companyUrl,
        tech_stack: techStack,
        source: "In-App Enterprise Plan Inquiry",
      };

      // Use the same lead capture endpoint as marketing website
      // but with authentication context from logged-in user
      return api.post("/api/v1/public/lead-capture", payload, {
        headers: {
          "Content-Type": "application/json",
          "X-Recaptcha-Token": token,
          "X-Recaptcha-Site-Key": config.recaptcha.siteKey,
          "X-Recaptcha-Action": "ENTERPRISE_INQUIRY",
          Referer: window.location.origin,
        },
      });
    },
  });

  function submitForm(values: z.infer<typeof formSchema>) {
    submitInquiry(values, {
      onSuccess: () => {
        // Combine key apps with other apps for analytics
        const techStack = [...values.keyApps];
        if (values.otherApps) {
          techStack.push(values.otherApps);
        }

        analytics.trackEnterpriseInquirySubmitted({
          companyName: values.companyName,
          companySize: values.companySize,
          industry: values.industry,
          jobTitle: values.jobTitle,
          techStack,
          enterpriseNeeds: values.enterpriseNeeds,
        });

        onSuccess?.();
      },
      onError: (err) => {
        // Handle specific error messages from backend
        const errorMessage = getApiErrorMessage(err);

        if (errorMessage === "Please enter your work email address.") {
          form.setError("email", {
            message: "Please enter your work email address.",
          });
          setCurrentStep(1);
        }

        // Handle other API errors
        setError(errorMessage);
      },
    });
  }

  const nextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      const fieldsToValidate = [
        "firstName",
        "lastName",
        "email",
        "jobTitle",
        "companyName",
        "companyUrl",
        "companySize",
        "industry",
      ] as const;
      form.trigger(fieldsToValidate).then((isValid) => {
        if (isValid) {
          setCurrentStep(2);
        }
      });
    } else if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        const values = form.getValues();
        return !!(
          values.firstName &&
          values.lastName &&
          values.email &&
          values.jobTitle &&
          values.companyName &&
          values.companyUrl &&
          values.companySize &&
          values.industry
        );
      case 2:
        const keyAppsValue = form.getValues().keyApps;
        return !!(keyAppsValue && keyAppsValue.length > 0);
      case 3:
        const enterpriseNeedsValue = form.getValues().enterpriseNeeds;
        return !!(enterpriseNeedsValue && enterpriseNeedsValue.trim());
      default:
        return false;
    }
  };

  const stepContent = getStepHeaderContent(currentStep);

  const colorClasses = {
    yellow: "bg-yellow-100 text-gold dark:bg-gold/10/30 dark:text-gold",
    blue: "bg-spectrum-blue-light/30 text-teal dark:bg-spectrum-blue-dark/20/30 dark:text-teal",
    purple: "bg-plum/10 text-plum dark:bg-plum/20/30 dark:text-plum",
    green:
      "bg-spectrum-green-light/30 text-lc-active dark:bg-spectrum-green-dark/20/30 dark:text-lc-active",
    red: "bg-red-100 text-lc-blocked dark:bg-spectrum-red-dark/20/30 dark:text-lc-blocked",
  };

  return (
    <div className="w-full">
      {/* Two-Column Layout */}
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
        {/* Left Column - Dynamic Header Content */}
        <div className="space-y-8 lg:sticky lg:top-8">
          <div className="space-y-6">
            <Badge className="inline-flex items-center gap-2">
              <div className="w-2 h-2 bg-n-00 rounded-full"></div>
              {stepContent?.badge} • Step {currentStep} of 3
            </Badge>

            <div className="space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                {stepContent?.title}
              </h1>

              <p className="text-lg text-muted-foreground">
                {stepContent?.subtitle}
              </p>
            </div>

            {/* Features List */}
            <div className="space-y-6">
              {stepContent?.features.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <div key={index} className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[feature.color as keyof typeof colorClasses]}`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-semibold text-foreground">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-muted-foreground">{stepContent?.cta}</p>
          </div>

          {/* Back/Previous Button */}
          <div className="pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={currentStep === 1 ? onClose : prevStep}
              className="cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentStep === 1 ? "Cancel" : "Previous"}
            </Button>
          </div>
        </div>

        {/* Right Column - Form Content */}
        <div className="w-full space-y-8">
          {/* Step Indicators */}
          <div className="flex justify-end">
            <div className="flex gap-2">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    step === currentStep
                      ? "bg-primary text-primary-foreground"
                      : step < currentStep
                        ? "bg-spectrum-green-light/30 text-lc-active dark:bg-spectrum-green-dark/20/30 dark:text-lc-active"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    step
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <Form {...form}>
            <form
              onSubmit={(e) => {
                // Prevent all form submissions - we handle this manually via button click
                e.preventDefault();
                return false;
              }}
              className="space-y-8"
            >
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="flex items-center gap-2 mb-2 text-foreground">
                      <Building className="w-5 h-5" />
                      Organization Information
                    </h2>
                  </div>

                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-medium">
                              First Name
                            </FormLabel>
                            <FormControl>
                              <Input
                                data-1p-ignore
                                placeholder="John"
                                className="h-12 text-base focus:border-primary transition-colors"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-medium">
                              Last Name
                            </FormLabel>
                            <FormControl>
                              <Input
                                data-1p-ignore
                                placeholder="Smith"
                                className="h-12 text-base focus:border-primary transition-colors"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Work Email
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-1p-ignore
                              type="email"
                              placeholder="john@company.com"
                              className="h-12 text-base focus:border-primary transition-colors"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="jobTitle"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Job Title
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-1p-ignore
                              placeholder="VP of Operations"
                              className="h-12 text-base focus:border-primary transition-colors"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companyName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Company Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-1p-ignore
                              placeholder="Acme Corporation"
                              className="h-12 text-base focus:border-primary transition-colors"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companyUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Company URL
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-1p-ignore
                              placeholder="https://company.com"
                              className="h-12 text-base focus:border-primary transition-colors"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="companySize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-medium">
                              Company Size
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl className="w-full">
                                <SelectTrigger className="h-12 text-base focus:border-primary bg-background text-foreground">
                                  <SelectValue placeholder="Select company size" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background">
                                {companySizes.map((size) => (
                                  <SelectItem
                                    key={size}
                                    value={size}
                                    className="text-foreground focus:bg-muted focus:text-foreground"
                                  >
                                    {size}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="industry"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-medium">
                              Industry
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl className="w-full">
                                <SelectTrigger className="h-12 text-base focus:border-primary bg-background text-foreground">
                                  <SelectValue placeholder="Select industry" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background">
                                {industries.map((industry) => (
                                  <SelectItem
                                    key={industry}
                                    value={industry}
                                    className="text-foreground focus:bg-muted focus:text-foreground"
                                  >
                                    {industry}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="flex items-center gap-2 mb-2 text-foreground">
                      <Briefcase className="w-5 h-5" />
                      Select the apps your teams use
                    </h2>
                  </div>

                  <div className="space-y-6">
                    {/* App Selection Grid */}
                    <FormField
                      control={form.control}
                      name="keyApps"
                      render={({ field }) => (
                        <FormItem>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {apps.map((app) => {
                              const isSelected = field.value?.includes(app.id);
                              return (
                                <div
                                  key={app.id}
                                  className={`relative p-3 border rounded-lg cursor-pointer transition-all ${
                                    isSelected
                                      ? "border-primary bg-primary/5 shadow-sm"
                                      : "hover:border-primary/50 hover:bg-primary/5"
                                  }`}
                                  onClick={() => {
                                    const updatedApps = isSelected
                                      ? field.value.filter(
                                          (id) => id !== app.id,
                                        )
                                      : [...field.value, app.id];
                                    field.onChange(updatedApps);
                                  }}
                                >
                                  {/* Selection Indicator */}
                                  {isSelected && (
                                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-lc-active rounded-full flex items-center justify-center">
                                      <CheckCircle className="w-3 h-3 text-white" />
                                    </div>
                                  )}

                                  {/* App Content - Horizontal Layout */}
                                  <div className="flex items-center gap-3">
                                    {/* App Icon */}
                                    <div className="w-8 h-8 flex-shrink-0">
                                      <img
                                        src={app.iconUrl}
                                        alt={app.name}
                                        className="w-full h-full object-contain"
                                      />
                                    </div>

                                    {/* App Name */}
                                    <span className="text-sm font-medium text-foreground">
                                      {app.name}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <FormMessage className="text-xs mt-2" />
                        </FormItem>
                      )}
                    />

                    {/* Other Apps Input */}
                    <FormField
                      control={form.control}
                      name="otherApps"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Other priority apps not listed above (Optional)
                          </FormLabel>
                          <FormControl>
                            <Input
                              data-1p-ignore
                              placeholder="e.g., Asana, Trello, Monday.com, etc."
                              className="h-12 text-base focus:border-primary transition-colors mt-2"
                              {...field}
                            />
                          </FormControl>
                          <p className="text-sm text-muted-foreground mt-1">
                            List any other important tools your team uses daily
                          </p>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="flex items-center gap-2 mb-2 text-foreground">
                      <Lightbulb className="w-5 h-5" />
                      Tell us your Enterprise requirements
                    </h2>
                  </div>

                  <FormField
                    control={form.control}
                    name="enterpriseNeeds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-medium">
                          Enterprise Requirements and Goals
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={`Tell us about your Enterprise requirements, security needs, compliance requirements, scaling challenges, and specific features...

Examples:
• SSO integration (SAML, OKTA, Azure AD)
• SOC2 compliance and data residency requirements
• Custom integrations with proprietary systems
• Advanced user management and permissions
• Dedicated support and SLA requirements
• Volume pricing and usage models
• Specific regulatory compliance needs`}
                            rows={8}
                            className="text-base focus:border-primary transition-colors resize-none"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-sm text-muted-foreground mt-2">
                          The more details you share about your Enterprise
                          needs, the better we can tailor our proposal to your
                          organization.
                        </p>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Next/Submit Button */}
              <div className="pt-6">
                <div className="flex justify-end">
                  {currentStep < 3 ? (
                    <Button
                      type="button"
                      onClick={nextStep}
                      disabled={!isStepValid()}
                      className="cursor-pointer"
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => {
                        // Validate the form first
                        form.handleSubmit((validatedValues) => {
                          submitForm(validatedValues);
                        })();
                      }}
                      isLoading={isPending}
                      className="cursor-pointer"
                    >
                      Submit Enterprise Inquiry
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {error && (
                <div className="text-center text-xs text-destructive mt-4">
                  {error}
                </div>
              )}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
